/* RESPONSIBLE TEAM: team-inbox-experience */
import { type SortParams } from 'embercom/services/inbox-api';
import { type SortState } from 'embercom/components/inbox2/conversations-table';

/**
 * Validates and transforms sort parameters for the search API.
 *
 * The search API supports both predefined sort keys and descriptor IDs (numeric strings
 * like "8622283" for custom/scorecard attributes). This utility ensures that only supported
 * sort fields are sent to the search API, falling back to 'relevance' for unsupported fields.
 *
 * Supported sort keys (from backend build_search_v2_query.rb):
 * - relevance
 * - sorting_updated_at
 * - created_at
 * - user_visible_updated_at
 * - ticket_id
 * - ticket_type
 * - custom_state
 * - Any numeric string (descriptor ID for custom/scorecard attributes)
 */

// Predefined sort_key values from backend (build_search_v2_query.rb)
export const SUPPORTED_SEARCH_SORT_FIELDS = Object.freeze([
  'relevance',
  'sorting_updated_at',
  'created_at',
  'user_visible_updated_at',
  'ticket_id',
  'ticket_type',
  'custom_state',
  'qa_notes_count',
] as const);

export type SupportedSearchSortField = (typeof SUPPORTED_SEARCH_SORT_FIELDS)[number];

/**
 * Checks if a sort field is a descriptor ID (numeric string).
 * Descriptor IDs are used for custom and scorecard conversation attributes.
 */
function isDescriptorId(sortField: string): boolean {
  return /^\d+$/.test(sortField);
}

const DEFAULT_SEARCH_SORT = {
  sort_field: 'relevance',
  sort_direction: 'desc',
} as const;

/**
 * Validates sort parameters for search API and returns valid params or defaults.
 *
 * @param sortParams - The requested sort parameters
 * @returns Valid sort parameters or default fallback
 */
export function validateSearchSortParams(sortParams: SortParams): SortParams {
  if (
    SUPPORTED_SEARCH_SORT_FIELDS.includes(sortParams.sort_field as SupportedSearchSortField) ||
    isDescriptorId(sortParams.sort_field)
  ) {
    return sortParams;
  }

  // Return a new object instead of frozen constant to avoid immutability issues
  return {
    sort_field: DEFAULT_SEARCH_SORT.sort_field,
    sort_direction: DEFAULT_SEARCH_SORT.sort_direction,
  };
}

/**
 * Converts SortState (from table UI) to SortParams (for API) with validation.
 *
 * @param sortState - The sort state from the table UI
 * @returns Valid sort parameters for search API
 */
export function convertAndValidateSortState(sortState: SortState): SortParams {
  let sortParams: SortParams = {
    sort_field: sortState.sortField,
    sort_direction: sortState.direction,
  };

  return validateSearchSortParams(sortParams);
}

/**
 * Checks if a sort field is supported by the search API.
 *
 * @param sortField - The sort field to check
 * @returns true if the sort field is supported
 */
export function isSearchSortFieldSupported(sortField: string): boolean {
  return (
    SUPPORTED_SEARCH_SORT_FIELDS.includes(sortField as SupportedSearchSortField) ||
    isDescriptorId(sortField)
  );
}

/**
 * Gets information about whether a fallback occurred and what the fallback values are.
 * Useful for logging or displaying warnings.
 *
 * @param requestedParams - The originally requested sort parameters
 * @returns Object containing whether fallback occurred and the values
 */
export function getSearchSortFallbackInfo(requestedParams: SortParams): {
  didFallback: boolean;
  requested: SortParams;
  actual: SortParams;
} {
  let actual = validateSearchSortParams(requestedParams);
  let didFallback =
    actual.sort_field !== requestedParams.sort_field ||
    actual.sort_direction !== requestedParams.sort_direction;

  return {
    didFallback,
    requested: requestedParams,
    actual,
  };
}
